const ROOT_CLASS = 'taa-premium-product-ready';
const GALLERY_SELECTOR = 'media-gallery';
const initializedGalleries = new WeakSet();
const initializedVideos = new WeakSet();

function normalizeLabel(value = '') {
  const clean = value.trim().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  if (!clean) return 'Product view';

  const lower = clean.toLowerCase();
  const lifestyle = /\b(lifestyle|model|person|woman|women|man|men|wearing|worn)\b/.test(lower);

  if (lower.includes('lifestyle front') || (lifestyle && /\bfront\b/.test(lower))) return 'Lifestyle Front';
  if (lower.includes('lifestyle back') || (lifestyle && /\bback\b/.test(lower))) return 'Lifestyle Back';
  if (lower.includes('hero front') || /\bfront\b/.test(lower)) return 'Hero Front';
  if (lower.includes('hero back') || /\bback\b/.test(lower)) return 'Hero Back';
  if (lower.includes('left side') || lower.includes('left view')) return 'Left Side';
  if (lower.includes('right side') || lower.includes('right view')) return 'Right Side';
  if (lower.includes('folded')) return 'Folded';
  if (lower.includes('collar') || lower.includes('neck detail')) return 'Collar Detail';
  if (lower.includes('fabric') || lower.includes('texture')) return 'Fabric Texture';
  if (lower.includes('sleeve')) return 'Sleeve Detail';
  if (lower.includes('woven label') || lower.includes('woven tag')) return 'Woven Label';
  if (lower.includes('hem')) return 'Hem Detail';
  if (lower.includes('packaging') || lower.includes('package')) return 'Packaging';
  if (lower.includes('video')) return 'Video';
  if (lower.includes('360') || lower.includes('spin')) return '360° Spin';
  if (lifestyle) return 'Lifestyle Front';

  return clean;
}

function getGallerySlides(gallery) {
  const slideshow = gallery.querySelector('slideshow-container');
  const slides = slideshow ? [...slideshow.querySelectorAll('slideshow-slide')] : [];
  return slides.length ? slides : [...gallery.querySelectorAll('[ref="media[]"]')];
}

function getSlideImage(slide) {
  return slide?.querySelector('img.product-media__image, img');
}

function getMediaDescriptor(slide) {
  const image = getSlideImage(slide);
  return `${image?.alt || ''} ${image?.currentSrc || image?.src || ''} ${slide?.dataset.mediaType || ''}`
    .trim()
    .toLowerCase();
}

function getMediaRank(slide) {
  const value = getMediaDescriptor(slide);
  const mediaType = slide?.matches('.product-media-container--video, .product-media-container--external_video')
    ? 'video'
    : slide?.matches('.product-media-container--model')
      ? 'model'
      : value;

  const lifestyle = /\b(lifestyle|model|person|woman|women|man|men|wearing|worn)\b/.test(value);

  if (value.includes('lifestyle front') || (lifestyle && /\bfront\b/.test(value))) return 10;
  if (value.includes('lifestyle back') || (lifestyle && /\bback\b/.test(value))) return 11;
  if (value.includes('hero front')) return 0;
  if (value.includes('hero back')) return 1;
  if (value.includes('left side') || value.includes('left view')) return 2;
  if (value.includes('right side') || value.includes('right view')) return 3;
  if (value.includes('folded')) return 4;
  if (value.includes('collar') || value.includes('neck detail')) return 5;
  if (value.includes('fabric texture') || value.includes('fabric detail') || value.includes('texture')) return 6;
  if (value.includes('sleeve')) return 7;
  if (value.includes('woven label') || value.includes('woven tag')) return 8;
  if (value.includes('hem')) return 9;
  if (value.includes('packaging') || value.includes('package')) return 12;
  if (value.includes('360') || value.includes('spin') || mediaType === 'model') return 14;
  if (mediaType === 'video' || value.includes('video')) return 13;

  // Generic front/back labels are intentionally evaluated after lifestyle/detail labels.
  if (/\bfront\b/.test(value)) return 0;
  if (/\bback\b/.test(value)) return 1;
  if (/\bleft\b/.test(value)) return 2;
  if (/\bright\b/.test(value)) return 3;
  if (lifestyle) return 10;

  return 99;
}

function applyCanonicalMediaOrder(gallery, slides) {
  const ranked = slides
    .map((slide, originalIndex) => ({ slide, originalIndex, rank: getMediaRank(slide) }))
    .sort((a, b) => a.rank - b.rank || a.originalIndex - b.originalIndex);

  ranked.forEach(({ slide, originalIndex, rank }) => {
    slide.style.order = String(rank * 1000 + originalIndex);
  });

  const orderedByOriginalIndex = new Map(ranked.map((item) => [item.originalIndex, item.rank * 1000 + item.originalIndex]));
  const controlGroups = [
    [...gallery.querySelectorAll('.slideshow-controls__thumbnail')],
    [...gallery.querySelectorAll('.slideshow-controls__dots > li')],
    [...gallery.querySelectorAll('.dialog-thumbnails-list__thumbnail')],
    [...gallery.querySelectorAll('.dialog-zoomed-gallery > li')],
    [...gallery.querySelectorAll('.media-gallery__grid > li')],
  ];

  controlGroups.forEach((items) => {
    items.forEach((item, index) => {
      const order = orderedByOriginalIndex.get(index);
      if (order !== undefined) item.style.order = String(order);
    });
  });

  return ranked.map(({ slide }) => slide);
}

function preloadImage(image) {
  if (!(image instanceof HTMLImageElement) || image.dataset.taaPreloaded === 'true') return;

  const source = image.currentSrc || image.src;
  if (!source) return;

  const preload = new Image();
  preload.decoding = 'async';
  preload.fetchPriority = 'low';
  preload.src = source;
  image.dataset.taaPreloaded = 'true';
}

function updateGalleryStatus(status, slide, index, count) {
  const label = normalizeLabel(getMediaDescriptor(slide));
  const labelElement = status.querySelector('.taa-gallery-status__label');
  const countElement = status.querySelector('.taa-gallery-status__count');

  if (labelElement) labelElement.textContent = label;
  if (countElement) countElement.textContent = `${index + 1} / ${count}`;
}

function setupGallery(gallery) {
  if (initializedGalleries.has(gallery)) return;
  initializedGalleries.add(gallery);

  gallery.tabIndex = gallery.tabIndex >= 0 ? gallery.tabIndex : 0;
  gallery.setAttribute('aria-label', gallery.getAttribute('aria-label') || 'Product media gallery');

  const slides = getGallerySlides(gallery);
  if (!slides.length) return;
  const visualSlides = applyCanonicalMediaOrder(gallery, slides);

  // Keep Horizon's native current-slide state aligned with the visually sorted first item.
  window.requestAnimationFrame(() => {
    const firstVisualIndex = slides.indexOf(visualSlides[0]);
    if (firstVisualIndex > 0 && gallery.slideshow && typeof gallery.slideshow.select === 'function') {
      gallery.slideshow.select(firstVisualIndex, undefined, { animate: false });
    }
  });

  const status = document.createElement('div');
  status.className = 'taa-gallery-status';
  status.setAttribute('aria-live', 'polite');
  status.innerHTML = '<span class="taa-gallery-status__label"></span><span class="taa-gallery-status__count"></span>';
  gallery.append(status);
  updateGalleryStatus(status, visualSlides[0], 0, visualSlides.length);
  preloadImage(getSlideImage(visualSlides[1]));

  const observer = new IntersectionObserver(
    (entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

      if (!visible) return;
      const visualIndex = visualSlides.indexOf(visible.target);
      if (visualIndex < 0) return;

      updateGalleryStatus(status, visualSlides[visualIndex], visualIndex, visualSlides.length);
      preloadImage(getSlideImage(visualSlides[(visualIndex + 1) % visualSlides.length]));
    },
    { root: gallery.querySelector('slideshow-container') || null, threshold: [0.55, 0.75, 0.95] }
  );

  slides.forEach((slide) => observer.observe(slide));

  gallery.addEventListener('keydown', (event) => {
    if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;

    const selected = visualSlides.findIndex((slide) => {
      const rect = slide.getBoundingClientRect();
      const galleryRect = gallery.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      return center >= galleryRect.left && center <= galleryRect.right;
    });
    const current = selected >= 0 ? selected : 0;
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const next = Math.max(0, Math.min(visualSlides.length - 1, current + direction));

    if (next === current) return;
    event.preventDefault();

    if (gallery.slideshow && typeof gallery.slideshow.select === 'function') {
      gallery.slideshow.select(slides.indexOf(visualSlides[next]));
    } else {
      visualSlides[next].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
  });

  const zoomDialog = gallery.querySelector('zoom-dialog');
  const dialog = zoomDialog?.querySelector('dialog');
  if (zoomDialog && dialog) {
    let wheelLocked = false;
    dialog.addEventListener(
      'wheel',
      (event) => {
        if (wheelLocked || Math.abs(event.deltaY) < 18) return;
        const originalThumbnails = [...dialog.querySelectorAll('.dialog-thumbnails-list__thumbnail')];
        if (originalThumbnails.length < 2) return;

        const visualThumbnails = [...originalThumbnails].sort(
          (a, b) => Number.parseInt(a.style.order || '0', 10) - Number.parseInt(b.style.order || '0', 10)
        );
        const active = visualThumbnails.findIndex((button) => button.getAttribute('aria-selected') === 'true');
        const direction = event.deltaY > 0 ? 1 : -1;
        const next = Math.max(0, Math.min(visualThumbnails.length - 1, Math.max(active, 0) + direction));
        if (next === active) return;

        const target = visualThumbnails[next];
        const originalIndex = originalThumbnails.indexOf(target);
        event.preventDefault();
        wheelLocked = true;
        if (typeof zoomDialog.handleThumbnailClick === 'function') {
          zoomDialog.handleThumbnailClick(originalIndex);
        } else {
          target?.click();
        }
        window.setTimeout(() => {
          wheelLocked = false;
        }, 280);
      },
      { passive: false }
    );
  }
}

function setupVideo(video) {
  if (!(video instanceof HTMLVideoElement) || initializedVideos.has(video)) return;
  initializedVideos.add(video);

  video.muted = true;
  video.playsInline = true;
  video.controls = true;
  video.preload = 'metadata';

  const observer = new IntersectionObserver(
    ([entry]) => {
      if (!entry) return;
      if (entry.isIntersecting && entry.intersectionRatio >= 0.55) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    },
    { threshold: [0, 0.55, 0.9] }
  );

  observer.observe(video);
}

function setupFooterGuard() {
  const sticky = document.querySelector('sticky-add-to-cart');
  const footer = document.querySelector('footer, .shopify-section-group-footer-group');
  if (!sticky || !footer || sticky.dataset.taaFooterGuard === 'true') return;

  sticky.dataset.taaFooterGuard = 'true';
  const observer = new IntersectionObserver(
    ([entry]) => {
      sticky.dataset.taaFooterVisible = entry?.isIntersecting ? 'true' : 'false';
    },
    { threshold: 0.01 }
  );
  observer.observe(footer);
}

function scan(root = document) {
  document.documentElement.classList.add(ROOT_CLASS);
  root.querySelectorAll?.(GALLERY_SELECTOR).forEach(setupGallery);
  root.querySelectorAll?.('.product-information video').forEach(setupVideo);
  setupFooterGuard();
}

scan();

const mutationObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    mutation.addedNodes.forEach((node) => {
      if (!(node instanceof Element)) return;
      if (node.matches(GALLERY_SELECTOR)) setupGallery(node);
      if (node.matches('.product-information video')) setupVideo(node);
      scan(node);
    });
  }
});

mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
document.addEventListener('shopify:section:load', (event) => scan(event.target));
